import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Menu, ShieldAlert, Search } from 'lucide-react';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { useAuthStore } from '@/stores/authStore';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';
import PaymentModal from '@/components/PaymentModal';
import SupportChat from '@/components/SupportChat';
import OnboardingFlowBar from '@/components/OnboardingFlowBar';
import PastDueBanner from '@/components/PastDueBanner';

export default function AppLayout() {
  const { status, fetchStatus } = useSubscriptionStore();
  const { impersonating, exitImpersonation } = useAuthStore();
  const navigate = useNavigate();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleExitImpersonation = async () => {
    setIsExiting(true);
    await exitImpersonation();
    navigate('/admin/users');
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonating]); // Re-fetch on mount and when impersonation changes

  const handleUpgradeClick = () => {
    setIsPaymentModalOpen(true);
  };

  return (
    <div className="md:flex h-screen bg-slate-50">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Impersonation Banner */}
        {impersonating && (
          <div className="bg-purple-600 text-white px-4 py-2 flex items-center justify-between text-sm z-50">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 shrink-0" />
              <span>
                Estás viendo la cuenta de <strong>{impersonating.userName}</strong> como administrador
              </span>
            </div>
            <button
              onClick={handleExitImpersonation}
              disabled={isExiting}
              className="ml-4 px-3 py-1 bg-white text-purple-600 rounded font-semibold hover:bg-purple-50 transition-colors text-xs shrink-0"
            >
              {isExiting ? 'Saliendo...' : 'Salir de impersonación'}
            </button>
          </div>
        )}

        {/* Email Verification Banner */}
        <EmailVerificationBanner />

        {/* Header */}
        {status?.on_trial ? (
          <TrialHeader
            daysRemaining={status.trial_days_remaining}
            onUpgrade={handleUpgradeClick}
            onMenuClick={() => setIsSidebarOpen(true)}
          />
        ) : (
          <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 gap-2">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="md:hidden text-slate-600 hover:text-slate-900 transition-colors p-2"
            >
              <Menu className="w-6 h-6" />
            </button>
            {/* Search trigger — opens Command Palette */}
            <button
              onClick={() => {
                document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
              }}
              className="flex items-center gap-2 sm:gap-3 px-3 py-1.5 text-sm text-slate-400 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors border border-slate-200 ml-auto"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Buscar...</span>
              <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-white rounded border border-slate-200">
                ⌘K
              </kbd>
            </button>
          </header>
        )}

        {/* Onboarding Flow Bar */}
        <OnboardingFlowBar />

        {/* Main Content */}
        <main className="flex-1 overflow-auto pb-16 md:pb-0">
          <AnimatedOutlet />
        </main>
      </div>

      {/* AI Support Chat */}
      <SupportChat />

      {/* Bottom Navigation (mobile) */}
      <BottomNav onMenuClick={() => setIsSidebarOpen(true)} />

      {/* Past Due Payment Modal */}
      {status?.is_past_due && <PastDueBanner />}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={() => {
          setIsPaymentModalOpen(false);
          fetchStatus();
        }}
      />
    </div>
  );
}

function AnimatedOutlet() {
  const location = useLocation();
  return (
    <div key={location.pathname} className="animate-in fade-in-0 duration-200">
      <Outlet />
    </div>
  );
}

function TrialHeader({ daysRemaining, onUpgrade, onMenuClick }: { daysRemaining: number; onUpgrade: () => void; onMenuClick: () => void }) {
  // Color scheme based on days remaining
  const getColorScheme = () => {
    if (daysRemaining <= 3) {
      // Urgent - Red
      return {
        gradient: 'from-red-500 to-rose-600',
        text: 'text-white',
        subtext: 'text-red-100',
        button: 'bg-white text-red-600 hover:bg-red-50',
        message: 'Tu prueba termina pronto',
      };
    } else if (daysRemaining <= 7) {
      // Warning - Orange
      return {
        gradient: 'from-amber-500 to-orange-500',
        text: 'text-white',
        subtext: 'text-amber-100',
        button: 'bg-white text-amber-600 hover:bg-amber-50',
        message: 'Activa tu plan para no perder acceso',
      };
    } else {
      // Calm - Blue
      return {
        gradient: 'from-blue-500 to-indigo-500',
        text: 'text-white',
        subtext: 'text-blue-100',
        button: 'bg-white text-blue-600 hover:bg-blue-50',
        message: 'Explora todas las funcionalidades',
      };
    }
  };

  const colors = getColorScheme();

  return (
    <header className={`h-14 bg-gradient-to-r ${colors.gradient} flex items-center justify-between px-4 md:px-8`}>
      <div className="flex items-center gap-3">
        {/* Hamburger button for mobile */}
        <button
          onClick={onMenuClick}
          className="md:hidden text-white hover:text-white/80 transition-colors p-2"
        >
          <Menu className="w-6 h-6" />
        </button>
        <span className={`${colors.text} text-lg font-semibold`}>
          Prueba gratuita: {daysRemaining} {daysRemaining === 1 ? 'día' : 'días'} restantes
        </span>
        <span className={`${colors.subtext} text-sm hidden sm:inline`}>
          {colors.message}
        </span>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Search trigger */}
        <button
          onClick={() => {
            document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
          }}
          className="flex items-center gap-2 px-2.5 sm:px-3 py-1.5 text-sm text-white/70 bg-white/15 hover:bg-white/25 rounded-lg transition-colors border border-white/20"
        >
          <Search className="w-4 h-4" />
          <span className="hidden sm:inline">Buscar...</span>
          <kbd className="hidden sm:flex px-1.5 py-0.5 text-[10px] font-medium text-white/50 bg-white/10 rounded border border-white/20">
            ⌘K
          </kbd>
        </button>
        <button
          onClick={onUpgrade}
          className={`px-5 py-2 ${colors.button} rounded-lg font-semibold transition-colors shadow-sm`}
        >
          Elegir plan
        </button>
      </div>
    </header>
  );
}
